import { Autocomplete, createFilterOptions, TextField } from "@mui/material";
import { uniqBy } from "lodash";
import { Controller } from "react-hook-form";
import { FormInputProps } from "../../models/form.model";

const filter = createFilterOptions();

export default function FormAutoSearch<T>(
  props: FormInputProps & {
    title?: string;
    options: T[];
    onAdd?: (value: string) => Promise<T>;
    multiple?: boolean;
    labelKey: string;
    valueKey: string;
    disabled?: boolean;
    loading?: boolean;
  },
) {
  const {
    name,
    control,
    title,
    label,
    required,
    helperText,
    multiple,
    rules,
    disabled,
    options,
    loading,
    valueKey,
    labelKey,
    onAdd,
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange, value = multiple ? [] : null },
        fieldState: { error },
      }) => {
        return (
          <Autocomplete
            value={value}
            multiple={multiple}
            disabled={disabled}
            filterSelectedOptions
            onChange={async (_event, newValue) => {
              if (
                typeof newValue === "string" ||
                (newValue && newValue.inputValue)
              ) {
                if (onAdd) {
                  const v = await onAdd(newValue.inputValue || newValue);
                  onChange(v);
                }
              } else {
                onChange(newValue);
              }
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              if (onAdd && params.inputValue !== "") {
                filtered.push({
                  inputValue: params.inputValue,
                  label: `Add ${title || label} "${params.inputValue}"`,
                });
              }
              return filtered;
            }}
            options={uniqBy(options, valueKey)}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                const op = options.find(
                  (a: any) => a[valueKey] === option
                ) as any;
                return op ? op[labelKey] : option;
              }
              if (option.inputValue) {
                return option.inputValue;
              }
              return option[labelKey];
            }}
            renderOption={(optionProps, option, { index }) => {
              const key = option[valueKey]
              return (
                <li {...optionProps} key={key} data-testid={`${name}-option${index}-${key}`}>
                  {option[labelKey] ||
                    option[valueKey] ||
                    option.label}
                </li>
              );
            }}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            freeSolo
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                required={required}
                helperText={error ? error.message : helperText || null}
                error={!!error}
                inputProps={{
                  ...params.inputProps,
                  "data-testid": name,
                  "data-value": value ? Array.isArray(value) ? value.map(v => v[valueKey]).join(',') : value[valueKey] : undefined,
                }}
              />
            )}
          />
        );
      }}
    />
  );
}
